import './FeedEditPhotoSource.css'
import { useState } from 'react'
import { Button } from '@/components/action/Button'
import { IconButton } from '@/components/action/IconButton'
import { Notice } from '@/components/action/Notice'
import { TabBar } from '@/components/action/TabBar'

const FTP_SERVER_HOST = 'ingest-ftp.aevocam.com'
const FTP_SERVER_URL = `ftp://${FTP_SERVER_HOST}`

export type PhotoSourceMode = 'ftp' | 'homeAssistant'

export type FeedEditPhotoSourceProps = {
  canEdit: boolean
  feedId: string | undefined
  tenantId: string | undefined
  feedDbId: string | number | undefined
  generatedPasscode: string | null
  generatingPasscode: boolean
  onGeneratePasscode: () => void
  onCopyPasscode: () => void
  onHidePasscode: () => void
  onCopyToClipboard: (title: string, text: string) => void
}

//--------------------------------------------------------------------------------
export function FeedEditPhotoSource({
  canEdit,
  feedId,
  tenantId,
  feedDbId,
  generatedPasscode,
  generatingPasscode,
  onGeneratePasscode,
  onCopyPasscode,
  onHidePasscode,
  onCopyToClipboard,
}: FeedEditPhotoSourceProps) {
  const [mode, setMode] = useState<PhotoSourceMode>('ftp')
  const [deviceCodeCopied, setDeviceCodeCopied] = useState(false)
  const username = `${tenantId}.${feedDbId}`
  const usernameReady = tenantId != null && feedDbId != null

  function renderPasscodeBlock(showDeviceCode: boolean) {
    const deviceCode =
      generatedPasscode != null && usernameReady
        ? `${username}/${generatedPasscode}`
        : null

    const hideIsPrimary = !showDeviceCode || deviceCodeCopied

    return (
      <div className='PasscodeBlock'>

        <div className='Wrapped'>

          <Button
            label='New passcode'
            icon='unlock'
            busy={generatingPasscode}
            disabled={!canEdit || feedId === 'new'}
            onClick={() => {
              setDeviceCodeCopied(false)
              onGeneratePasscode()
            }}
          />

          {generatedPasscode && (<>

            <Button
              label='Copy it'
              icon='copy'
              onClick={onCopyPasscode}
            />

            <Button
              variant={hideIsPrimary ? 'primary' : undefined}
              label='Hide it'
              icon='lock'
              onClick={() => {
                setDeviceCodeCopied(false)
                onHidePasscode()
              }}
            />

          </>)}

        </div>

        {generatedPasscode != null && (
          <>

            <Notice variant='info'>
              Here is your 12-digit passcode. Do not use spaces.
            </Notice>

            <div className='PasscodeDisplay'>
              <span className='Part'>{generatedPasscode.slice(0, 3)}</span><span className='Part'>{generatedPasscode.slice(3, 6)}</span><span className='Part'>{generatedPasscode.slice(6, 9)}</span><span className='Part'>{generatedPasscode.slice(9, 12)}</span>
            </div>

            {showDeviceCode && deviceCode != null && (
              <div className='DeviceCode'>
                <Button
                  variant={deviceCodeCopied ? undefined : 'primary'}
                  label='Copy device code'
                  icon='copy'
                  onClick={() => {
                    setDeviceCodeCopied(true)
                    onCopyToClipboard('Device code', deviceCode)
                  }}
                />
              </div>
            )}

            <Notice variant='warning'>
              You will not see this passcode here again. Apply it to
              your camera right now or save it somewhere secure.
              Click &quot;Hide it&quot; when you are done.
            </Notice>

          </>
        )}

      </div>
    )
  }

  return (
    <div className='FeedEditPhotoSource'>

      <TabBar<PhotoSourceMode>
        ariaLabel='Photo source'
        options={[
          { value: 'ftp', label: 'FTP' },
          { value: 'homeAssistant', label: 'Home Assistant' },
        ]}
        value={mode}
        setValue={setMode}
      />

      {mode === 'ftp' && (
        <>
          <h3>FTP passcode</h3>
          {renderPasscodeBlock(false)}

          <h3>FTP settings</h3>

          <table className='Settings'><tbody>
            <tr>
              <th>Mode:</th>
              <td>PASV</td>
            </tr>
            <tr>
              <th>Port:</th>
              <td>21 (standard)</td>
            </tr>
            <tr>
              <th>Server host name:</th>
              <td>
                {FTP_SERVER_HOST}
                <IconButton
                  icon='copy'
                  tooltip='Copy to clipboard'
                  onClick={() => onCopyToClipboard('Host name', FTP_SERVER_HOST)}
                />
              </td>
            </tr>
            <tr>
              <th>Server as URL:</th>
              <td>
                {FTP_SERVER_URL}
                <IconButton
                  icon='copy'
                  tooltip='Copy to clipboard'
                  onClick={() => onCopyToClipboard('FTP URL', FTP_SERVER_URL)}
                />
              </td>
            </tr>
            <tr>
              <th>Username:</th>
              <td>
                {username}
                <IconButton
                  icon='copy'
                  tooltip='Copy to clipboard'
                  disabled={tenantId == null || feedDbId == null}
                  onClick={() => onCopyToClipboard('Username', username)}
                />
              </td>
            </tr>
            <tr>
              <th>Password:</th>
              <td><i>(use &quot;New passcode&quot; above)</i></td>
            </tr>
          </tbody></table>
        </>
      )}

      {mode === 'homeAssistant' && (
        <>
          <h3>Passcode</h3>
          {renderPasscodeBlock(true)}

          <h3>Settings</h3>

          <table className='Settings'><tbody>
            <tr>
              <th>Username:</th>
              <td>
                {username}
                <IconButton
                  icon='copy'
                  tooltip='Copy to clipboard'
                  disabled={tenantId == null || feedDbId == null}
                  onClick={() => onCopyToClipboard('Username', username)}
                />
              </td>
            </tr>
            <tr>
              <th>Passcode:</th>
              <td><i>(use &quot;New passcode&quot; above)</i></td>
            </tr>
          </tbody></table>
        </>
      )}

    </div>
  )
}
